import React, { useState, useEffect, useRef } from 'react';
import { Send, CheckCircle2, Loader2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPopup = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Popup Card - Frosted Glass Effect */}
      <div className="relative bg-[rgba(26,28,30,0.85)] backdrop-blur-xl border border-[rgba(197,155,47,0.3)] rounded-2xl p-8 max-w-md w-full shadow-[0_20px_60px_rgba(197,155,47,0.3)] animate-scale-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[rgb(161,161,170)] hover:text-white transition-colors duration-200"
        >
          <X size={24} />
        </button>

        {/* Success Animation */}
        <div className="text-center">
          <div className="relative inline-block mb-6">
            {/* Animated circles */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[rgb(124,56,178)] to-[rgb(197,155,47)] opacity-20 animate-ping"></div>
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.2)] to-[rgba(124,56,178,0.2)] border-2 border-[rgb(197,155,47)] flex items-center justify-center animate-bounce-in">
              <CheckCircle2 size={40} className="text-[rgb(197,155,47)] animate-check" />
            </div>
          </div>

          <h3 className="text-3xl font-bold text-white mb-3 animate-slide-up">
            Message Sent!
          </h3>
          <p className="text-[rgb(218,218,218)] text-lg mb-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Thank you for reaching out.
          </p>
          <p className="text-[rgb(161,161,170)] animate-slide-up" style={{ animationDelay: '0.2s' }}>
            We'll get back to you within 24 hours.
          </p>

          {/* Decorative gradient line */}
          <div className="mt-6 h-1 w-full bg-gradient-to-r from-transparent via-[rgb(197,155,47)] to-transparent rounded-full animate-expand"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
        @keyframes check {
          0% {
            transform: scale(0) rotate(-45deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes expand {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-scale-in { animation: scale-in 0.4s ease-out; }
        .animate-bounce-in { animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55); }
        .animate-check { animation: check 0.5s ease-out 0.3s both; }
        .animate-slide-up { animation: slide-up 0.5s ease-out both; }
        .animate-expand { animation: expand 0.8s ease-out 0.4s both; }
      `}</style>
    </div>
  );
};

const ContactForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setShowSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        setErrors({});
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setErrors({
        submit: 'Failed to send message. Please try again or email us directly at eshwaldsystems@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section 
        id="contact" 
        ref={sectionRef}
        className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden"
      >
        {/* Background decoration - Gold glow */}
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-[rgba(197,155,47,0.06)] blur-[120px] rounded-full"></div>

        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}>
              <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-sm font-semibold mb-4">
                <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
                <span>GET IN TOUCH</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Transform</span> Your Creator Business?
              </h2>
              <p className="text-xl text-[rgb(218,218,218)] mb-8 leading-relaxed">
                Let's discuss how we can help you build sustainable income streams from your audience. No upfront cost, no risk — just growth.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.3)] to-[rgba(124,56,178,0.3)] border border-[rgb(197,155,47)] flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 size={14} className="text-[rgb(197,155,47)]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1">Quick Response Time</div>
                    <div className="text-[rgb(161,161,170)] text-sm">We'll get back to you within 24 hours</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.3)] to-[rgba(124,56,178,0.3)] border border-[rgb(197,155,47)] flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 size={14} className="text-[rgb(197,155,47)]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1">Free Consultation</div>
                    <div className="text-[rgb(161,161,170)] text-sm">No obligations, just honest advice</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.3)] to-[rgba(124,56,178,0.3)] border border-[rgb(197,155,47)] flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 size={14} className="text-[rgb(197,155,47)]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1">Tailored Solutions</div>
                    <div className="text-[rgb(161,161,170)] text-sm">Custom strategies for your unique audience</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}>
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(197,155,47,0.3)] transition-colors duration-300 rounded-2xl p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-medium mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full bg-[rgb(38,40,42)] border-2 ${
                        errors.name ? 'border-red-500' : 'border-[rgb(63,63,63)]'
                      } rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(197,155,47)] transition-colors duration-200`}
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full bg-[rgb(38,40,42)] border-2 ${
                        errors.email ? 'border-red-500' : 'border-[rgb(63,63,63)]'
                      } rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(197,155,47)] transition-colors duration-200`}
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className={`w-full bg-[rgb(38,40,42)] border-2 ${
                        errors.message ? 'border-red-500' : 'border-[rgb(63,63,63)]'
                      } rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(197,155,47)] transition-colors duration-200 resize-none`}
                      placeholder="Tell us about your creator business..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-500">{errors.message}</p>
                    )}
                  </div>

                  {errors.submit && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <p className="text-sm text-red-400">{errors.submit}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-[rgb(17,17,19)] px-6 py-4 rounded-xl font-semibold text-lg hover:from-[rgb(224,194,95)] hover:to-[rgb(197,155,47)] transition-all duration-300 hover:transform hover:translate-y-[-2px] hover:scale-105 flex items-center justify-center space-x-2 shadow-[0_0_30px_rgba(197,155,47,0.4)] hover:shadow-[0_0_50px_rgba(197,155,47,0.6)] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send size={20} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Popup */}
      <SuccessPopup isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </>
  );
};

export default ContactForm;
